import { useCallback, useState } from "react";

import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { CanvasView, GRID_CONSTANTS } from "store/types";

const { CANVAS_WIDTH, CANVAS_HEIGHT } = GRID_CONSTANTS;

interface UsePdfExportProps {
  canvasRef: React.RefObject<HTMLDivElement>;
  pages: CanvasView[][];
  currentPageIndex: number;
  reportTitle: string;
  setCurrentPageIndex: (index: number) => void;
  setError: (error: string | null) => void;
}

interface UsePdfExportReturn {
  exporting: boolean;
  handleExportPdf: () => Promise<void>;
}

// Wait for all images (img tags and CSS background-images) to finish loading
async function waitForImages(element: HTMLElement): Promise<void> {
  const promises: Promise<void>[] = [];

  element.querySelectorAll("img").forEach((img) => {
    if (!img.complete) {
      promises.push(
        new Promise((resolve) => {
          img.onload = () => resolve();
          img.onerror = () => resolve();
        })
      );
    }
  });

  element.querySelectorAll<HTMLElement>("*").forEach((el) => {
    const bg = getComputedStyle(el).backgroundImage;
    const match = bg?.match(/url\(["']?(.+?)["']?\)/);
    if (match?.[1] && !match[1].startsWith("data:")) {
      const img = new Image();
      promises.push(
        new Promise((resolve) => {
          img.onload = () => resolve();
          img.onerror = () => resolve();
          img.src = match[1];
        })
      );
    }
  });

  await Promise.all(promises);
}

// Apply export styles directly to the live DOM element, returns a restore function
function applyExportStyles(element: HTMLElement): () => void {
  const restoreFns: Array<() => void> = [];

  // Helper to save and override style
  const setStyle = (el: HTMLElement, prop: string, value: string) => {
    const original = el.style.getPropertyValue(prop);
    el.style.setProperty(prop, value);
    restoreFns.push(() => el.style.setProperty(prop, original));
  };

  // Remove border radius and shadow from canvas paper
  setStyle(element, "border-radius", "0");
  setStyle(element, "box-shadow", "none");

  // Remove grid background pattern
  const gridArea = element.querySelector("[data-export-grid]") as HTMLElement | null;
  if (gridArea) {
    setStyle(gridArea, "background-image", "none");
  }

  // Remove dashed header border
  const header = element.querySelector("[data-export-header]") as HTMLElement | null;
  if (header) {
    setStyle(header, "border-bottom", "none");
  }

  // Hide export indicator
  const exportIndicator = element.querySelector("[data-export-indicator]") as HTMLElement | null;
  if (exportIndicator) {
    setStyle(exportIndicator, "display", "none");
  }

  // Hide card action buttons
  element.querySelectorAll(".card-actions").forEach((el) => {
    setStyle(el as HTMLElement, "display", "none");
  });

  // Hide Highcharts tooltips
  element.querySelectorAll(".highcharts-tooltip, .highcharts-tooltip-box").forEach((el) => {
    setStyle(el as HTMLElement, "display", "none");
  });

  // Fix Highcharts SVG text fonts
  const fontFamily = "-apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica, Arial, sans-serif";
  element.querySelectorAll("svg text, svg tspan").forEach((el) => {
    const textEl = el as SVGTextElement;
    const originalAttr = textEl.getAttribute("font-family");
    const originalStyle = textEl.style.fontFamily;
    textEl.setAttribute("font-family", fontFamily);
    textEl.style.fontFamily = fontFamily;
    restoreFns.push(() => {
      if (originalAttr) textEl.setAttribute("font-family", originalAttr);
      else textEl.removeAttribute("font-family");
      textEl.style.fontFamily = originalStyle;
    });
  });

  return () => {
    restoreFns.forEach((fn) => fn());
  };
}

// Draw footer directly on the PDF using jsPDF native drawing API
function addPdfFooter(pdf: jsPDF, pageNum: number, totalPages: number): void {
  const pdfWidth = pdf.internal.pageSize.getWidth();
  const pdfHeight = pdf.internal.pageSize.getHeight();
  const footerHeight = 40;
  const footerY = pdfHeight - 25;

  // White background for footer area
  pdf.setFillColor(255, 255, 255);
  pdf.rect(0, pdfHeight - footerHeight, pdfWidth, footerHeight, "F");

  // Blue dot
  pdf.setFillColor(59, 130, 246);
  pdf.circle(30, footerY, 2, "F");

  // "Built by altius.capital"
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(7);
  pdf.setTextColor(30, 41, 59);
  pdf.text("Built by altius.capital", 36, footerY + 1.5);

  // "Confidential. For internal use."
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(6);
  pdf.setTextColor(148, 163, 184);
  pdf.text("Confidential. For internal use.", 36, footerY + 10);

  // Page chip - border rounded rect with "Page X of Y"
  const pageText = `Page ${pageNum} of ${totalPages}`;
  pdf.setFontSize(7);
  pdf.setTextColor(100, 116, 139);
  const textWidth = pdf.getTextWidth(pageText);
  const chipPadding = 8;
  const chipWidth = textWidth + chipPadding * 2;
  const chipHeight = 14;
  const chipX = pdfWidth - 30 - chipWidth;
  const chipY = footerY - 4;

  pdf.setDrawColor(226, 232, 240);
  pdf.setLineWidth(0.5);
  pdf.roundedRect(chipX, chipY, chipWidth, chipHeight, 4, 4, "S");
  pdf.text(pageText, chipX + chipPadding, chipY + 9.5);
}

export const usePdfExport = ({
  canvasRef,
  pages,
  currentPageIndex,
  reportTitle,
  setCurrentPageIndex,
  setError
}: UsePdfExportProps): UsePdfExportReturn => {
  const [exporting, setExporting] = useState(false);

  const handleExportPdf = useCallback(async () => {
    if (!canvasRef.current || pages.every((page) => page.length === 0)) return;

    setExporting(true);

    try {
      // Wait for all images to finish loading before capture
      await waitForImages(canvasRef.current);

      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "pt",
        format: "a4"
      });

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const originalPageIndex = currentPageIndex;

      const nonEmptyPages = pages.filter((page) => page.length > 0);
      const totalPages = nonEmptyPages.length;
      let currentPdfPage = 0;

      for (let pageIndex = 0; pageIndex < pages.length; pageIndex++) {
        if (pages[pageIndex].length === 0) continue;

        currentPdfPage++;

        // Switch to the page we want to capture
        if (pageIndex !== currentPageIndex) {
          setCurrentPageIndex(pageIndex);
          await new Promise((resolve) => setTimeout(resolve, 100));
        }

        // Apply export styles to live DOM
        const restore = applyExportStyles(canvasRef.current);

        // Capture the current view directly (no cloning)
        const canvas = await html2canvas(canvasRef.current, {
          scale: 2,
          useCORS: true,
          allowTaint: true,
          backgroundColor: "#ffffff",
          logging: false,
          width: CANVAS_WIDTH,
          height: CANVAS_HEIGHT
        });

        // Restore original styles immediately after capture
        restore();

        const ratio = pdfWidth / canvas.width;
        const scaledWidth = pdfWidth;
        const scaledHeight = canvas.height * ratio;

        if (currentPdfPage > 1) {
          pdf.addPage();
        }

        const imgData = canvas.toDataURL("image/png");
        pdf.addImage(imgData, "PNG", 0, 0, scaledWidth, scaledHeight);

        // Draw footer directly on the PDF
        addPdfFooter(pdf, currentPdfPage, totalPages);
      }

      // Restore original page
      if (originalPageIndex !== currentPageIndex) {
        setCurrentPageIndex(originalPageIndex);
      }

      const fileName = `${reportTitle || "Report"}_${new Date().toISOString().split("T")[0]}.pdf`;
      pdf.save(fileName);
    } catch (error) {
      console.error("Failed to export PDF:", error);
      setError("Failed to export PDF. Please try again.");
    } finally {
      setExporting(false);
    }
  }, [pages, currentPageIndex, reportTitle, setCurrentPageIndex, setError, canvasRef]);

  return { exporting, handleExportPdf };
};
